import {
  Table,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  MenuItem,
  Button,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
  Select,
  Box,
  Menu
} from '@material-ui/core';
import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import JsonDataModal, { isJsonData } from './modalTable';
import ErrorIcon from '@material-ui/icons/Error';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import TableIcon from '@material-ui/icons/Toc';
import SearchIcon from '@material-ui/icons/Search';
import CodeIcon from '@material-ui/icons/Code';

const useStyles = makeStyles({
  table: {
    tableLayout: 'auto',
    width: '100%',
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #e0e0e0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 56
    },
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: 0,
      zIndex: 20
    },
    '& .MuiTableHead-root .MuiTableRow-root': {
      backgroundColor: '#e8e8e8'
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      backgroundColor: '#e8e8e8',
      boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.1)'
    },
    '& tbody tr:nth-of-type(odd)': {
      backgroundColor: '#fafafa'
    },
    '& tbody tr:nth-of-type(even)': {
      backgroundColor: '#fff'
    }
  },
  stickyCol0: {
    position: 'sticky',
    left: 0,
    zIndex: 21,
    backgroundColor: '#fff !important'
  },
  stickyCol1: {
    position: 'sticky',
    left: 52,
    zIndex: 21,
    backgroundColor: '#fff !important'
  },
  stickyCol0Body: {
    position: 'sticky',
    left: 0
  },
  stickyCol1Body: {
    position: 'sticky',
    left: 52
  }
});

const headerCellStyle = {
  fontWeight: 600,
  backgroundColor: 'white',
  fontSize: '10px',
  textTransform: 'Capitalize',
  letterSpacing: '0.02em',
  textAlign: 'center',
  padding: '20px  ',
  whiteSpace: 'nowrap',
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const getBodyCellStyle = align => ({
  fontSize: '10px',
  textAlign: align,
  padding: '10px',
  whiteSpace: 'nowrap',
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
});

const formatNumber = value => {
  if (value === null || value === undefined || value === '') return '--';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const isNumeric = value => {
  if (value === null || value === undefined || value === '') return '--';
  if (typeof value === 'boolean') return false;
  const num = parseFloat(value);
  return !isNaN(num) && isFinite(num);
};

//FORMATTING
const DATE_COLUMN_KEYS = ['dueDate', 'jDate', 'sked_date'];
const STRING_COLUMN_KEYS = ['sked_desc', 'simDescription'];
const PLAIN_NUMBER_COLUMN_KEYS = ['profile_contactNo'];
const PERCENT_COLUMN_KEYS = [
  'sked_percent_4267',
  'sked_percent_4268',
  'sked_percent_4266'
];

// Header = column key as-is (kung ano sa JSON, ganun sa header)
const getHeaderFromKey = key => (key ? String(key) : '');

// Infer alignment based on sampled values (numeric => right, otherwise left)
const inferAlignForKey = (key, rows) => {
  if (!key) return 'left';
  for (let i = 0; i < rows.length; i += 1) {
    const value = rows[i] && rows[i][key];
    if (value === null || value === undefined || value === '') continue;
    return isNumeric(value) ? 'right' : 'left';
  }
  return 'left';
};

// Build column definitions from data: No. + all keys in order as they appear in JSON.
const buildColumnsFromRows = rows => {
  const baseColumns = [{ header: 'No.', key: null, align: 'right' }];
  if (!Array.isArray(rows) || !rows.length) {
    return baseColumns;
  }
  const seen = new Set();
  const keyOrder = [];
  rows.forEach(row => {
    if (!row || typeof row !== 'object') return;
    Object.keys(row).forEach(key => {
      if (!seen.has(key)) {
        seen.add(key);
        keyOrder.push(key);
      }
    });
  });
  return [
    ...baseColumns,
    ...keyOrder.map(key => ({
      header: getHeaderFromKey(key),
      key,
      align: inferAlignForKey(key, rows)
    }))
  ];
};

// Get cell value from row; derive sked_desc / sked_date from skedValues when missing
const getCellValue = (row, key) => {
  if (!key) return undefined;
  const raw = row[key];
  if (raw !== undefined && raw !== null && raw !== '') return raw;
  if (key === 'sked_desc' && row.skedValues) {
    const parts = String(row.skedValues).split('|');
    return parts[2] !== undefined ? parts[2].trim() : raw;
  }
  if (key === 'sked_date' && row.skedValues) {
    const parts = String(row.skedValues).split('|');
    return parts[0] !== undefined ? parts[0].trim() : raw;
  }
  return raw;
};

const formatDate = value => {
  if (value === null || value === undefined || value === '') return '--';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const hasTime = /T\d{1,2}:/.test(String(value));
  return hasTime
    ? d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
    : d.toLocaleDateString(undefined, { dateStyle: 'short' });
};

const formatCell = (value, colKey) => {
  if (value === null || value === undefined || value === '') return '--';

  // Special handling for pipe-delimited schedule strings like:
  // "2026-02-01|3855.0000|Installment 13 - Feb"
  if (typeof value === 'string' && value.includes('|')) {
    const parts = value.split('|').map(p => p.trim());
    // For skedValues: date | amount | description
    if (colKey === 'skedValues') {
      const [date, amount, desc] = parts;
      return [date, amount, desc].filter(Boolean).join(' – ');
    }
    // For skedCurrent: amount | rebate
    if (colKey === 'skedCurrent') {
      const [amount, rebate] = parts;
      return [amount, rebate && `Rebate: ${rebate}`]
        .filter(Boolean)
        .join(' – ');
    }
  }

  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return String(value);
  if (DATE_COLUMN_KEYS.includes(colKey)) return formatDate(value);
  if (STRING_COLUMN_KEYS.includes(colKey)) return String(value);
  if (colKey === 'termsDays' && isNumeric(value))
    return String(Number(value)) + ' day/s';
  if (PLAIN_NUMBER_COLUMN_KEYS.includes(colKey) && isNumeric(value))
    return String(Number(value));
  if (PERCENT_COLUMN_KEYS.includes(colKey) && isNumeric(value))
    return String(Number(value)) + '%';
  return isNumeric(value) ? formatNumber(value) : value;
};

const PIPE_VALUE_LABELS = {
  skedValues: ['Date', 'Amount', 'Description'],
  skedCurrent: ['Amount', 'Rebate'],
  skedValues_4266: ['Amount', 'Rebate', 'Balance']
};

const getPipeValueObject = (value, colKey) => {
  if (typeof value !== 'string' || !value.includes('|')) return null;

  const parts = value.split('|').map(part => part.trim());
  const labels =
    PIPE_VALUE_LABELS[colKey] || parts.map((_, index) => `Value ${index + 1}`);

  return parts.reduce((acc, part, index) => {
    acc[labels[index] || `Value ${index + 1}`] = part || '--';
    return acc;
  }, {});
};

const ROWS_PER_PAGE = 20;

function getErrorLocations(rows) {
  if (!Array.isArray(rows)) return [];
  const locations = [];
  rows.forEach((row, rowIndex) => {
    const errors = row && row.errors;
    if (!errors || typeof errors !== 'object') return;
    const errorTypes = ['not-equal', 'gt', 'gte', 'lt', 'lte', 'startswith'];
    errorTypes.forEach(errorType => {
      const fields = errors[errorType];
      if (!fields || typeof fields !== 'object') return;
      Object.entries(fields).forEach(([fieldKey, detail]) => {
        if (detail !== undefined && detail !== null) {
          locations.push({
            rowIndex,
            fieldKey,
            errorType,
            detail: Array.isArray(detail) ? detail : [detail]
          });
        }
      });
    });
  });
  return locations;
}

function valueMatchesExpected(currentVal, expectedVal) {
  if (expectedVal === undefined || expectedVal === null) return false;
  if (currentVal === expectedVal) return true;
  if (typeof expectedVal === 'number' && Number.isFinite(expectedVal)) {
    const n = Number(currentVal);
    return !Number.isNaN(n) && n === expectedVal;
  }
  return String(currentVal).trim() === String(expectedVal).trim();
}

export function TableAGR({ mode, executData, onChangeField, ...rest }) {
  const classes = useStyles();
  const exe = executData?.exe;
  const executeAGR = rest?.executeAGR;
  const ixSim = rest?.ixSim;
  const handleExeSimulator = rest?.handleExeSimulator;

  const DBSettings = rest?.settings?.DBSettings;
  const agr = DBSettings?.jsonSettings?.agr;
  const simulator = agr?.simulator;
  const fileSimData = rest?.data;

  const [executeLoading, setExecuteLoading] = useState(false);
  const [executeError, setExecuteError] = useState(null);
  const [reportMeta, setReportMeta] = useState({});

  const [executePage, setExecutePage] = useState(0);
  const [hasExecutedOnce, setHasExecutedOnce] = useState(false);

  const loadExecute = () => {
    handleExeSimulator(ixSim);
    setHasExecutedOnce(true);
  };

  const [saveTableData, setSaveTableData] = useState(() =>
    Array.isArray(fileSimData?.data)
      ? JSON.parse(JSON.stringify(fileSimData.data))
      : []
  );

  const saveOriginalRef = useRef(null);
  if (saveOriginalRef.current === null && saveTableData.length > 0) {
    saveOriginalRef.current = JSON.parse(JSON.stringify(saveTableData));
  }
  const [savePage, setSavePage] = useState(0);
  const [editingCell, setEditingCell] = useState(null);

  const savePaginated = saveTableData.slice(
    savePage * ROWS_PER_PAGE,
    savePage * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  const [errorFocusExecute, setErrorFocusExecute] = useState(null);
  const [errorFocusSave, setErrorFocusSave] = useState(null);
  const executeRowRefs = useRef({});
  const saveRowRefs = useRef({});
  const saveTableSectionRef = useRef(null);
  const executeTableSectionRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedColumnName, setSelectedColumnName] = useState('');
  const [errorMenuAnchorExecute, setErrorMenuAnchorExecute] = useState(null);
  const [edit, setEdit] = useState(false);

  const errorLocationsExecute = getErrorLocations(exe);
  const errorCountExecute =
    reportMeta?.count?.error ?? errorLocationsExecute?.length;

  const handleExecutePageChange = (event, newPage) => setExecutePage(newPage);
  const handleSavePageChange = (event, newPage) => setSavePage(newPage);

  const handleCellClick = (value, columnName) => {
    const parsedPipeValue = getPipeValueObject(value, columnName);
    const modalData = isJsonData(value) ? value : parsedPipeValue;
    if (modalData) {
      setSelectedData(modalData);
      setSelectedColumnName(columnName);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedData(null);
    setSelectedColumnName('');
  };

  const openErrorMenuExecute = e => {
    if (errorLocationsExecute.length)
      setErrorMenuAnchorExecute(e.currentTarget);
  };
  const closeErrorMenuExecute = () => setErrorMenuAnchorExecute(null);

  // ...existing code...
  const goToErrorInResultTable = location => {
    closeErrorMenuExecute();
    setErrorFocusExecute({
      rowIndex: location.rowIndex,
      fieldKey: location.fieldKey,
      errorType: location.errorType,
      detail: location.detail
    });
    const targetExecutePage = Math.floor(location.rowIndex / ROWS_PER_PAGE);
    setExecutePage(targetExecutePage);

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (executeTableSectionRef?.current) {
          executeTableSectionRef.current.scrollIntoView({
            block: 'start',
            behavior: 'smooth'
          });
        }

        setTimeout(() => {
          const rowEl = executeRowRefs.current?.[location.rowIndex];
          if (rowEl) {
            rowEl.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }, 300);
      }, 0);
    });
  };

  const getColumnHeader = fieldKey => {
    return getHeaderFromKey(fieldKey);
  };

  const handleCellDoubleClick = (rowIndex, colKey) => {
    if (colKey != null) setEditingCell({ rowIndex, colKey });
  };
  const handleCellEditChange = (rowIndex, colKey, newValue) => {
    setSaveTableData(prev => {
      const updated = prev.map((row, i) =>
        i === rowIndex ? { ...row, [colKey]: newValue } : row
      );

      onChangeField(updated);

      return updated;
    });
  };

  const handleCellEditBlur = () => setEditingCell(null);

  const renderExecuteRow = (agr, index) => {
    const rowIndex = executePage * ROWS_PER_PAGE + index;
    const isOddRow = rowIndex % 2 === 0;
    const isErrorRow =
      errorFocusExecute && errorFocusExecute.rowIndex === rowIndex;
    return (
      <TableRow
        key={rowIndex}
        ref={el => {
          executeRowRefs.current[rowIndex] = el;
        }}
        style={{ backgroundColor: isErrorRow ? '#ffcdd2' : undefined }}
      >
        {buildColumnsFromRows(exe).map((col, i) => {
          const value =
            col.key === null ? rowIndex + 1 : getCellValue(agr, col.key);
          const display =
            col.key === null
              ? String(rowIndex + 1)
              : formatCell(value, col.key);
          const parsedPipeValue = getPipeValueObject(value, col.key);
          const clickable = isJsonData(value) || Boolean(parsedPipeValue);
          const isErrorCell =
            isErrorRow &&
            errorFocusExecute &&
            col.key === errorFocusExecute.fieldKey;
          const expectedVal =
            isErrorCell &&
            errorFocusExecute.detail &&
            errorFocusExecute.detail.length >= 2
              ? errorFocusExecute.detail[1]
              : isErrorCell &&
                errorFocusExecute.detail &&
                errorFocusExecute.detail.length === 1
              ? errorFocusExecute.detail[0]
              : null;
          const expectedDisplay =
            expectedVal !== null && expectedVal !== undefined
              ? typeof expectedVal === 'object'
                ? JSON.stringify(expectedVal)
                : String(expectedVal)
              : '';
          const openModalFromIcon = e => {
            e.stopPropagation();
            handleCellClick(value, col.header);
          };
          return (
            <TableCell
              key={i}
              className={
                i === 0
                  ? classes.stickyCol0Body
                  : i === 1
                  ? classes.stickyCol1Body
                  : undefined
              }
              style={{
                ...getBodyCellStyle(col.align),
                backgroundColor: isErrorCell
                  ? '#ef9a9a'
                  : isOddRow
                  ? '#fafafa'
                  : '#fff',
                verticalAlign:
                  isErrorCell && expectedDisplay ? 'top' : undefined
              }}
              title={
                isErrorCell && expectedDisplay
                  ? `Expected: ${expectedDisplay}`
                  : typeof display === 'string'
                  ? display
                  : String(display)
              }
            >
              {clickable ? (
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <TableIcon
                    fontSize="small"
                    color="primary"
                    style={{ cursor: 'pointer' }}
                    onClick={openModalFromIcon}
                    titleAccess="Expand"
                  />
                  Expand
                </span>
              ) : (
                <>
                  {display}
                  {isErrorCell && expectedDisplay && (
                    <div
                      style={{
                        fontSize: '9px',
                        color: '#c62828',
                        marginTop: 4,
                        fontWeight: 600
                      }}
                    >
                      Expected:{' '}
                      {expectedDisplay.length > 40
                        ? expectedDisplay.slice(0, 40) + '…'
                        : expectedDisplay}
                    </div>
                  )}
                </>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  const renderSaveRow = (agr, index) => {
    const rowIndex = savePage * ROWS_PER_PAGE + index;
    const isOddRow = rowIndex % 2 === 0;
    const isErrorRow = errorFocusSave && errorFocusSave.rowIndex === rowIndex;
    const expectedForRow =
      isErrorRow && errorFocusSave?.detail?.length >= 2
        ? errorFocusSave.detail[1]
        : null;
    const rowIsCorrect =
      isErrorRow &&
      expectedForRow != null &&
      valueMatchesExpected(
        getCellValue(agr, errorFocusSave?.fieldKey),
        expectedForRow
      );
    const rowBg = rowIsCorrect
      ? '#c8e6c9'
      : isErrorRow
      ? '#ffcdd2'
      : isOddRow
      ? '#fafafa'
      : '#fff';

    return (
      <TableRow
        key={rowIndex}
        ref={el => {
          saveRowRefs.current[rowIndex] = el;
        }}
        style={{ backgroundColor: rowBg }}
      >
        {buildColumnsFromRows(saveTableData).map((col, i) => {
          const value =
            col.key === null ? rowIndex + 1 : getCellValue(agr, col.key);
          const display =
            col.key === null
              ? String(rowIndex + 1)
              : formatCell(value, col.key);
          const parsedPipeValue = getPipeValueObject(value, col.key);
          const clickable = isJsonData(value) || Boolean(parsedPipeValue);
          const isEditing =
            editingCell &&
            editingCell.rowIndex === rowIndex &&
            editingCell.colKey === col.key;
          const canEdit = col.key != null;
          const cellIsError =
            isErrorRow && errorFocusSave && col.key === errorFocusSave.fieldKey;
          const expectedVal =
            cellIsError &&
            errorFocusSave.detail &&
            errorFocusSave.detail.length >= 2
              ? errorFocusSave.detail[1]
              : cellIsError &&
                errorFocusSave.detail &&
                errorFocusSave.detail.length === 1
              ? errorFocusSave.detail[0]
              : null;
          const expectedDisplay =
            expectedVal !== null && expectedVal !== undefined
              ? typeof expectedVal === 'object'
                ? JSON.stringify(expectedVal)
                : String(expectedVal)
              : '';
          const cellIsCorrect =
            cellIsError &&
            expectedVal != null &&
            valueMatchesExpected(value, expectedVal);
          const openModalFromIcon = e => {
            e.stopPropagation();
            handleCellClick(value, col.header);
          };
          const cellBg = rowIsCorrect
            ? '#a5d6a7'
            : cellIsCorrect
            ? '#a5d6a7'
            : cellIsError
            ? '#ef9a9a'
            : isErrorRow
            ? '#ffcdd2'
            : isOddRow
            ? '#fafafa'
            : '#fff';
          return (
            <TableCell
              key={i}
              className={
                i === 0
                  ? classes.stickyCol0Body
                  : i === 1
                  ? classes.stickyCol1Body
                  : undefined
              }
              style={{
                ...getBodyCellStyle(col.align),
                backgroundColor: cellBg,
                cursor: canEdit ? 'text' : 'default',
                padding: isEditing ? '4px' : '10px',
                verticalAlign: 'top'
              }}
              onDoubleClick={() => handleCellDoubleClick(rowIndex, col.key)}
              title={
                cellIsError && expectedDisplay
                  ? `Expected: ${expectedDisplay}`
                  : typeof display === 'string'
                  ? display
                  : String(display)
              }
            >
              {isEditing ? (
                <>
                  <input
                    autoFocus
                    value={saveTableData[rowIndex][col.key] ?? ''}
                    onChange={e =>
                      handleCellEditChange(rowIndex, col.key, e.target.value)
                    }
                    onBlur={handleCellEditBlur}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.target.blur();
                    }}
                    style={{
                      width: '100%',
                      border: '1px solid #1976d2',
                      borderRadius: 4,
                      padding: '4px 8px',
                      fontSize: '10px',
                      textAlign: col.align,
                      boxSizing: 'border-box'
                    }}
                  />
                  {(cellIsError || cellIsCorrect) && expectedDisplay && (
                    <div
                      style={{
                        fontSize: '9px',
                        color: cellIsCorrect ? '#2e7d32' : '#c62828',
                        marginTop: 4,
                        fontWeight: 600
                      }}
                    >
                      Expected:{' '}
                      {expectedDisplay.length > 50
                        ? expectedDisplay.slice(0, 50) + '…'
                        : expectedDisplay}
                    </div>
                  )}
                </>
              ) : clickable ? (
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <TableIcon
                    fontSize="small"
                    color="primary"
                    style={{ cursor: 'pointer' }}
                    onClick={openModalFromIcon}
                    titleAccess="Expand"
                  />
                  Expand
                </span>
              ) : (
                <>
                  {display}
                  {(cellIsError || cellIsCorrect) && expectedDisplay && (
                    <div
                      style={{
                        fontSize: '9px',
                        color: cellIsCorrect ? '#2e7d32' : '#c62828',
                        marginTop: 4,
                        fontWeight: 600
                      }}
                    >
                      Expected:{' '}
                      {expectedDisplay.length > 40
                        ? expectedDisplay.slice(0, 40) + '…'
                        : expectedDisplay}
                    </div>
                  )}
                </>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  return (
    <div
    // style={{
    //   padding: '12px 16px',
    //   flex: 1,
    //   minHeight: 0,
    //   display: 'flex',
    //   flexDirection: 'column',
    //   boxSizing: 'border-box',
    //   alignItems: 'center',
    //   overflow: 'hidden'
    // }}
    >
      <div
      // style={{
      //   display: 'flex',
      //   flexDirection: 'row',
      //   gap: 16,
      //   width: '100%',
      //   maxWidth: 1600,
      //   margin: '0 auto',
      //   flex: 1,
      //   minHeight: 0,
      //   overflow: 'hidden'
      // }}
      >
        {mode === 'dashboard' ? (
          <>
            <Box display="flex" flexDirection="column" gap={1}>
              {/* Search on top */}
              <Box marginBottom={1}>
                {simulator?.ixSim?.length > 1 && (
                  <TextField
                    size="small"
                    label="Search"
                    variant="outlined"
                    onChange={''} // keep your handler
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Select
                            value={ixSim}
                            onChange={e => handleExeSimulator(e.target.value)}
                            disableUnderline
                          >
                            {simulator?.ixSim?.map((sim, idx) => (
                              <MenuItem key={idx} value={sim}>
                                {sim}
                              </MenuItem>
                            ))}
                          </Select>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              </Box>

              <>
                {/* Button and chip row */}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  marginBottom={1}
                  gap={1}
                >
                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      // size="small"
                      onClick={loadExecute}
                      disabled={executeLoading}
                      // startIcon={<CachedIcon style={{ fontSize: 20 }} />}
                    >
                      {executeLoading ? 'Re-validating...' : 'Re-validate'}
                    </Button>
                    <Button
                      style={{ marginLeft: 8 }}
                      variant={edit ? 'outlined' : 'contained'}
                      color="primary"
                      // size="small"
                      onClick={setEdit}
                      disabled={executeLoading}
                      startIcon={edit && <CodeIcon style={{ fontSize: 20 }} />}
                    >
                      {edit ? 'You are in edit mode' : 'Edit'}
                    </Button>
                  </Box>

                  <Box>
                    <Chip
                      icon={
                        <ErrorIcon style={{ fontSize: 20, color: '#f44336' }} />
                      }
                      label={`${errorCountExecute} Error${
                        errorCountExecute !== 1 ? 's' : ''
                      }`}
                      onClick={openErrorMenuExecute}
                      onDelete={
                        errorLocationsExecute.length
                          ? openErrorMenuExecute
                          : undefined
                      }
                      deleteIcon={
                        errorLocationsExecute.length ? (
                          <ArrowDropDownIcon />
                        ) : (
                          undefined
                        )
                      }
                      disabled={!errorCountExecute}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      style={{
                        borderColor: 'rgba(244, 67, 54, 0.5)',
                        color: '#c62828'
                      }}
                    />
                    <Menu
                      anchorEl={errorMenuAnchorExecute}
                      open={Boolean(errorMenuAnchorExecute)}
                      onClose={closeErrorMenuExecute}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      getContentAnchorEl={null}
                      PaperProps={{ style: { maxHeight: 320 } }}
                    >
                      {errorLocationsExecute.map((loc, idx) => (
                        <MenuItem
                          key={`${loc.rowIndex}-${loc.fieldKey}-${idx}`}
                          onClick={() => goToErrorInResultTable(loc)}
                        >
                          Row {loc.rowIndex + 1} ·{' '}
                          {getColumnHeader(loc.fieldKey)} · {loc.errorType}
                          {loc.detail && loc.detail.length
                            ? ` (${loc.detail.join(' vs ')})`
                            : ''}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                </Box>
              </>
            </Box>
            <Paper
              ref={executeTableSectionRef}
              // elevation={3}
              // style={{
              //   borderRadius: 8,
              //   overflow: 'hidden',
              //   flex: 1,
              //   minWidth: 0,
              //   minHeight: 0,
              //   height: '100%',
              //   display: 'flex',
              //   flexDirection: 'column',
              //   position: 'relative'
              // }}
            >
              {executeError && (
                <div
                  style={{
                    padding: '8px 24px',
                    background: '#ffebee',
                    color: '#c62828',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12
                  }}
                >
                  <Typography variant="body2">{executeError}</Typography>
                  <Button size="small" color="primary" onClick={loadExecute}>
                    Retry
                  </Button>
                </div>
              )}
              {executeLoading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.7)',
                    zIndex: 10
                  }}
                >
                  <CircularProgress />
                </div>
              )}
              <>
                <TableContainer
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    backgroundColor: '#fff',
                    borderTop: '1px solid #e0e0e0'
                  }}
                >
                  <Table stickyHeader size="small" className={classes.table}>
                    <TableHead>
                      <TableRow>
                        {buildColumnsFromRows(exe).map((col, i) => (
                          <TableCell
                            key={i}
                            style={headerCellStyle}
                            title={col.header}
                            className={
                              i === 0
                                ? classes.stickyCol0
                                : i === 1
                                ? classes.stickyCol1
                                : undefined
                            }
                          >
                            {col.header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exe?.map((agr, index) => renderExecuteRow(agr, index))}
                      {/* {executeRows.map((agr, index) => null
                      // renderExecuteRow(agr, index)
                    )} */}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={exe?.length}
                  page={executePage}
                  onPageChange={handleExecutePageChange}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  labelDisplayedRows={({ page, count }) =>
                    `Page ${page + 1} of ${Math.max(
                      1,
                      Math.ceil(count / ROWS_PER_PAGE)
                    )}`
                  }
                />
              </>
            </Paper>
          </>
        ) : (
          <Paper
            ref={saveTableSectionRef}
            elevation={3}
            style={{
              borderRadius: 8,
              overflow: 'hidden',
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            <TableContainer
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                backgroundColor: '#fff',
                borderTop: '1px solid #e0e0e0'
              }}
            >
              <Table stickyHeader size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    {buildColumnsFromRows(saveTableData).map((col, i) => (
                      <TableCell
                        key={i}
                        style={headerCellStyle}
                        title={col.header}
                        className={
                          i === 0
                            ? classes.stickyCol0
                            : i === 1
                            ? classes.stickyCol1
                            : undefined
                        }
                      >
                        {col.header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {savePaginated.map((agr, index) => renderSaveRow(agr, index))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={saveTableData.length}
              page={savePage}
              onPageChange={handleSavePageChange}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
              labelDisplayedRows={({ page, count }) =>
                `Page ${page + 1} of ${Math.max(
                  1,
                  Math.ceil(count / ROWS_PER_PAGE)
                )}`
              }
            />
          </Paper>
        )}
      </div>

      <JsonDataModal
        key={
          modalOpen && selectedData ? JSON.stringify(selectedData) : 'closed'
        }
        open={modalOpen}
        onClose={handleModalClose}
        data={selectedData}
        columnName={selectedColumnName}
      />
    </div>
  );
}

export default TableAGR;
